/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { createContext, useContext, useEffect, useState } from "react";
import MsClarityClient from "../../../Management/Mandanten/Api/MsClarityClient";
import { environment } from "../../environment";

export interface ICookieBannerContext {
    visible: boolean;
    anzeigen: () => void;
    ermittleCookiesErlaubtFuerBuchungsportal: () => boolean | undefined;
    setzeCookies: (erlaubt: boolean) => void;
}

export interface CookieBannerContextProps {
    children: React.ReactNode;
}

const STORAGE_KEY_VERWALTUNG = "lan.cookies.clarity_verwaltung";
const STORAGE_KEY_BUCHUNGSPORTAL = "lan.cookies.clarity_buchungsportal";
const cookieBannerContext = createContext({} as ICookieBannerContext);

export default function CookieBannerContext(props: CookieBannerContextProps) {
    const [visible, setVisible] = useState(false);
    const [clarityId, setClarityId] = useState("");
    const msClarityClient = new MsClarityClient();

    function aktiviereMicrosoftClarityScript(attributes: { [qualifiedName: string]: string }, clarityId : string) {
        const script = Object.entries(attributes ?? {})
            .reduce(
                (script, [key, value]) => {
                    script.setAttribute(key, value as string);
                    return script;
                },
                document.createElement("script"));
        script.append(`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${clarityId}");`);
        return script;
    }

    function aktiviereMicrosoftClarity(clarityId : string) {
        document.head.prepend(aktiviereMicrosoftClarityScript({ type: "text/javascript" }, clarityId));
    }

    function deaktiviereMicrosoftClarity() {
        window.location.reload();
    }

    const ermittleCookiesErlaubtFuerBuchungsportal = (): boolean | undefined => {
        const item = window.localStorage.getItem(STORAGE_KEY_BUCHUNGSPORTAL);
        if (item === null) return undefined;
        return JSON.parse(item).erlaubt;
    }

    const setzeCookiesErlaubtFuerBuchungsportal = (cookiesErlaubt: boolean) => {
        window.localStorage.setItem(
            STORAGE_KEY_BUCHUNGSPORTAL,
            JSON.stringify({ erlaubt: cookiesErlaubt })
        );
    }

    const setzeCookiesErlaubtFuerVerwaltung = (cookiesErlaubt: boolean) => {
        window.localStorage.setItem(
            STORAGE_KEY_VERWALTUNG,
            JSON.stringify({ erlaubt: cookiesErlaubt })
        );
    }

    useEffect(() => {
        msClarityClient.ladeMsClarity().then(clarity => setClarityId(clarity.msClarityProjectId));
    }, []);

    useEffect(() => {
        const cookiesErlaubtFuerBuchungsportal = ermittleCookiesErlaubtFuerBuchungsportal();
        if (environment.istBuchungsportal && cookiesErlaubtFuerBuchungsportal === undefined && clarityId !== '') {
            setVisible(true);
        }

        if (!environment.istBuchungsportal && clarityId !== '') {
            setzeCookiesErlaubtFuerVerwaltung(true);
            setzeCookiesErlaubtFuerBuchungsportal(true);
            aktiviereMicrosoftClarity(clarityId);
        }

        if (environment.istBuchungsportal && cookiesErlaubtFuerBuchungsportal === true && clarityId !== '') {
            aktiviereMicrosoftClarity(clarityId);
        }
    }, [environment.istBuchungsportal, clarityId]);

    const context: ICookieBannerContext = {
        visible,
        anzeigen() {
            setVisible(true);
        },
        ermittleCookiesErlaubtFuerBuchungsportal,
        setzeCookies(erlaubt: boolean) {
            if (ermittleCookiesErlaubtFuerBuchungsportal() === erlaubt) {
                setVisible(false);
                return;
            }

            setzeCookiesErlaubtFuerBuchungsportal(erlaubt);

            if (erlaubt) {
                aktiviereMicrosoftClarity(clarityId);
            } else {
                deaktiviereMicrosoftClarity();
            }

            setVisible(false);
        },
    };

    return (
        <cookieBannerContext.Provider value={context}>
            {props.children}
        </cookieBannerContext.Provider>
    );
}

export function useCookieBannerContext(): ICookieBannerContext {
    return useContext(cookieBannerContext);
}
